import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import type { Kundenzeile } from '../api/kunden';
import {
  BEZUGSNAME,
  HANDLUNGSNAME,
  STATUSNAME,
  ZUORDNUNGSNAME,
  bestaetigen,
  bezugHinzu,
  bezugWeg,
  kundeLoesen,
  kundeSetzen,
  neuPruefen,
  vorbelegung as ladeVorbelegung,
  zuordnung as ladeZuordnung,
  type Kundenvorbelegung,
  type Sicherheit,
  type Verlaufszeile,
  type Vorgangsstatus,
  type Zuordnungsansicht,
  type Zuordnungsstand,
} from '../api/support';
import { Kundensuche } from '../buchung/Kundensuche';
import { KundeAnlegen, type Kundenvorgabe } from '../kunde/KundeAnlegen';
import { AbTablet } from '../komponenten/AbTablet';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { markenklasse, type Markenart } from '../marken';

/**
 * DIE ZUORDNUNG EINES VORGANGS ZU KUNDE UND BEZUG (SUP-07 bis SUP-13;
 * Auftrag CRM-177).
 *
 * Die Automatik ordnet beim Eingang zu und sagt, wie sicher sie ist. Passt
 * genau ein Kunde, aber nur über Name oder Domäne, ordnet sie ihn zu und
 * markiert ihn als unsicher (SUP-09, entschieden 10.10.2026): Bestätigen
 * oder Umordnen ist dann ein Klick. Was sie nicht eindeutig weiß, schlägt
 * sie vor (SUP-09) oder legt es in die Klärungsliste (SUP-13) — entschieden
 * wird hier, von einem Menschen. Jede Handlung steht im Verlauf (SUP-12).
 *
 * KEIN KUNDE ENTSTEHT VON SELBST (SUP-10): Vertrieb bietet „Als Interessent
 * anlegen", Info „Kunde anlegen" — beides erst auf Klick, vorbelegt aus
 * Absender und Signatur, mit dem Dublettenverdacht (KUN-23) davor.
 *
 * AUF DEM TELEFON lesen; zuordnen ab Tablet (CRM-106).
 */

const SICHERHEITSART: Readonly<Record<Sicherheit, Markenart>> = {
  sicher: 'abschluss',
  wahrscheinlich: 'laufend',
  unsicher: 'aufmerksamkeit',
};

/** SUP-17: der Bearbeitungsstand als Marke, im Posteingang und in der Kundenakte. */
export function statusmarke(status: Vorgangsstatus): JSX.Element {
  const art: Markenart = status === 'neu'
    ? 'aufmerksamkeit'
    : status === 'geloest' || status === 'geschlossen'
      ? 'abschluss'
      : 'laufend';
  return <span className={markenklasse(art)}>{STATUSNAME[status]}</span>;
}

/** SUP-07: wie der Vorgang zu seinem Kunden kam, als Marke in Liste und Ansicht. */
export function Zuordnungsmarke({
  zuordnung,
  sicherheit,
}: {
  readonly zuordnung: Zuordnungsstand;
  readonly sicherheit: Sicherheit | null;
}): JSX.Element {
  if (zuordnung === 'automatisch' && sicherheit !== null) {
    return <span className={markenklasse(SICHERHEITSART[sicherheit])}>{sicherheit}</span>;
  }
  const art: Markenart = zuordnung === 'bearbeiter'
    ? 'abschluss'
    : zuordnung === 'ausstehend' ? 'stillgelegt' : 'aufmerksamkeit';
  return <span className={markenklasse(art)}>{ZUORDNUNGSNAME[zuordnung]}</span>;
}

/** SUP-12: „automatisch, unsicher" — die Stufe gehört zur automatischen Zuordnung. */
function handlungstext(z: Verlaufszeile): string {
  return z.handlung === 'automatisch' && z.sicherheit !== null ? `automatisch, ${z.sicherheit}` : HANDLUNGSNAME[z.handlung];
}

/** SUP-12: der Grund, wenn eine unsichere Zuordnung ohne Rückfrage umgeordnet wird. */
const BERICHTIGT = 'Unsichere Zuordnung berichtigt';

function datum(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
  });
}

/** Was aus der Nachricht in die Kundenanlage geht: bei Firmen die Person als Ansprechpartner. */
function vorgabeAus(v: Kundenvorbelegung): Kundenvorgabe {
  if (v.kundenart === 'geschaeft') {
    return {
      kundenart: 'geschaeft',
      firmierung: v.firma,
      apVorname: v.vorname,
      apNachname: v.nachname,
      apEmail: v.email,
      apTelefon: v.telefon,
    };
  }
  return { kundenart: 'privat', vorname: v.vorname, nachname: v.nachname, email: v.email, telefon: v.telefon };
}

type Fenster =
  | { readonly art: 'suche' }
  | { readonly art: 'umordnen'; readonly kunde: Kundenzeile }
  | { readonly art: 'loesen' }
  | { readonly art: 'anlegen'; readonly vorbelegung: Kundenvorbelegung };

export function Zuordnungsbereich({
  vorgang,
  darfZuordnen,
  darfKundeAnlegen,
  beiKunde,
  beiAenderung,
}: {
  readonly vorgang: string;
  /** `support/zuordnen`. */
  readonly darfZuordnen: boolean;
  /** `kunde/anlegen` — für „Als Interessent anlegen" (SUP-10). */
  readonly darfKundeAnlegen: boolean;
  readonly beiKunde?: ((id: string) => void) | undefined;
  readonly beiAenderung: () => void;
}): JSX.Element {
  const [ansicht, setzeAnsicht] = useState<Zuordnungsansicht | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fenster, setzeFenster] = useState<Fenster | null>(null);
  const [grund, setzeGrund] = useState('');
  const [nummer, setzeNummer] = useState('');

  useEffect(() => {
    setzeAnsicht(null);
    setzeFehler(null);
    let gilt = true;
    ladeZuordnung(vorgang).then(
      (a) => {
        if (gilt) setzeAnsicht(a);
      },
      (f: unknown) => {
        if (!gilt) return;
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      },
    );
    return () => {
      gilt = false;
    };
  }, [vorgang]);

  const tue = useCallback(async (was: () => Promise<Zuordnungsansicht>): Promise<boolean> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      setzeAnsicht(await was());
      beiAenderung();
      return true;
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
      return false;
    } finally {
      setzeLaeuft(false);
    }
  }, [beiAenderung]);

  const schliessen = (): void => {
    setzeFenster(null);
    setzeGrund('');
  };

  const anlegenOeffnen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      setzeFenster({ art: 'anlegen', vorbelegung: await ladeVorbelegung(vorgang) });
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  if (ansicht === null) {
    return fehler === null ? <p role="status">Zuordnung wird geladen …</p> : <Meldung fehler={fehler} />;
  }
  const { kunde } = ansicht;
  const vertrieb = ansicht.kundenkreis === 'firmenkunden';
  // SUP-09: eine automatische Zuordnung, die nicht sicher ist, wartet auf den Blick eines Menschen.
  const zuPruefen = ansicht.zuordnung === 'automatisch' && ansicht.sicherheit !== null && ansicht.sicherheit !== 'sicher';
  const unsicher = ansicht.zuordnung === 'automatisch' && ansicht.sicherheit === 'unsicher';

  return (
    <section className="zuordnung" aria-label="Kunde und Bezüge">
      <h3>Kunde</h3>
      {fehler !== null && <Meldung fehler={fehler} />}
      {kunde === null ? (
        <p>
          <Zuordnungsmarke zuordnung={ansicht.zuordnung} sicherheit={null} />{' '}
          <span className="hilfe">
            {ansicht.zuordnung === 'vorschlag'
              ? 'Mehrere Kunden kommen in Frage. Bitte wählen.'
              : ansicht.zuordnung === 'klaerung'
                ? 'Kein Kunde passt zu Absender, Name, Signatur oder Belegnummer.'
                : 'Die Zuordnung läuft mit dem nächsten Abruf.'}
          </span>
        </p>
      ) : (
        <p>
          {beiKunde === undefined ? (
            <strong>{kunde.kundennummer} {kunde.name}</strong>
          ) : (
            <button type="button" className="knopf knopf--verweis" data-auch-telefon onClick={() => beiKunde(kunde.id)}>
              {kunde.kundennummer} {kunde.name}
            </button>
          )}{' '}
          <Zuordnungsmarke zuordnung={ansicht.zuordnung} sicherheit={ansicht.sicherheit} />
          {kunde.status === 'interessent' && <> <span className={markenklasse('aufmerksamkeit')}>Interessent</span></>}
          {ansicht.grund !== null && (
            <>
              <br />
              <span className="hilfe">{ansicht.grund}</span>
            </>
          )}
          {unsicher && (
            <>
              <br />
              <span className="hilfe">Nur Name oder Domäne passen. Bitte bestätigen oder einem anderen Kunden zuordnen.</span>
            </>
          )}
        </p>
      )}

      {ansicht.vorschlaege.length > 0 && (
        <div className="tabellenhuelle">
          <table className="liste__tabelle" aria-label="Vorschläge">
            <thead>
              <tr>
                <th scope="col">Vorschlag</th>
                <th scope="col">Woran erkannt</th>
                {darfZuordnen && <th scope="col"><span className="nur-vorlesen">Handlung</span></th>}
              </tr>
            </thead>
            <tbody>
              {ansicht.vorschlaege.map((v) => (
                <tr key={v.kunde}>
                  <th scope="row">
                    {v.kundennummer} {v.name}{' '}
                    <span className={markenklasse(SICHERHEITSART[v.sicherheit])}>{v.sicherheit}</span>
                  </th>
                  <td className="hilfe">{v.grund}</td>
                  {darfZuordnen && (
                    <td>
                      <AbTablet teil>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() => void tue(() => kundeSetzen(vorgang, v.kunde, null))}
                        >
                          Zuordnen
                        </button>
                      </AbTablet>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {darfZuordnen && (
        <AbTablet teil>
          <div className="knopfreihe">
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => setzeFenster({ art: 'suche' })}>
              {kunde === null ? 'Kunde suchen …' : 'Anderem Kunden zuordnen …'}
            </button>
            {kunde === null && darfKundeAnlegen && (
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void anlegenOeffnen()}>
                {vertrieb ? 'Als Interessent anlegen' : 'Kunde anlegen'}
              </button>
            )}
            {kunde !== null && (
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => setzeFenster({ art: 'loesen' })}>
                Zuordnung lösen
              </button>
            )}
            {ansicht.zuordnung !== 'bearbeiter' && (
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void tue(() => neuPruefen(vorgang))}>
                Neu prüfen
              </button>
            )}
            {zuPruefen && (
              <button
                type="button"
                className="knopf knopf--haupt knopf--klein"
                disabled={laeuft}
                onClick={() => void tue(() => bestaetigen(vorgang))}
              >
                Bestätigen
              </button>
            )}
          </div>
        </AbTablet>
      )}

      <h3>Bezüge</h3>
      {ansicht.bezuege.length === 0 ? (
        <p className="hilfe">Keine Buchungs-, Angebots-, Auftrags-, Rechnungs- oder Gutscheinnummer erkannt.</p>
      ) : (
        <ul className="zuordnung__bezuege">
          {ansicht.bezuege.map((b) => (
            <li key={b.id}>
              {BEZUGSNAME[b.art]} {b.nummer}
              {b.herkunft === 'erkannt' && <span className="hilfe"> · erkannt</span>}
              {darfZuordnen && (
                <AbTablet teil>
                  {' '}
                  <button
                    type="button"
                    className="knopf knopf--eingriff knopf--klein"
                    disabled={laeuft}
                    aria-label={`${BEZUGSNAME[b.art]} ${b.nummer} entfernen`}
                    onClick={() => void tue(() => bezugWeg(vorgang, b.id))}
                  >
                    Entfernen
                  </button>
                </AbTablet>
              )}
            </li>
          ))}
        </ul>
      )}
      {darfZuordnen && (
        <AbTablet teil>
          <form
            className="knopfreihe"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (nummer.trim() === '') return;
              void tue(() => bezugHinzu(vorgang, nummer)).then((ok) => {
                if (ok) setzeNummer('');
              });
            }}
          >
            <label className="nur-vorlesen" htmlFor={`bezug-${vorgang}`}>Belegnummer</label>
            <input
              id={`bezug-${vorgang}`}
              value={nummer}
              placeholder="z. B. RE-000123"
              maxLength={40}
              onChange={(e) => setzeNummer(e.target.value)}
            />
            <button type="submit" className="knopf knopf--klein" disabled={laeuft || nummer.trim() === ''}>
              Verknüpfen
            </button>
          </form>
        </AbTablet>
      )}

      {ansicht.verlauf.length > 0 && (
        <details>
          <summary>Verlauf der Zuordnung ({String(ansicht.verlauf.length)})</summary>
          <ol className="zuordnung__verlauf">
            {ansicht.verlauf.map((z, i) => (
              <li key={`${z.am}-${String(i)}`}>
                <span className="hilfe">{datum(z.am)} · {z.wer}</span>
                <br />
                {handlungstext(z)}
                {z.kundeVorher !== null && z.kundeNachher !== null && z.kundeVorher !== z.kundeNachher
                  && `: ${z.kundeVorher} → ${z.kundeNachher}`}
                {z.kundeVorher !== null && z.kundeVorher === z.kundeNachher && `: ${z.kundeNachher}`}
                {z.kundeVorher === null && z.kundeNachher !== null && `: ${z.kundeNachher}`}
                {z.kundeVorher !== null && z.kundeNachher === null && `: ${z.kundeVorher}`}
                {z.bezug !== null && `: ${z.bezug}`}
                {z.grund !== null && <span className="hilfe"> — {z.grund}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}

      {fenster?.art === 'suche' && (
        <Kundensuche
          beiSchliessen={schliessen}
          beiWahl={(k) => {
            // SUP-12: Wer umordnet, sagt warum; die erste Zuordnung braucht keinen Grund.
            // SUP-09: eine unsichere Zuordnung zu berichtigen ist ein Klick, der Grund steht fest.
            if (kunde !== null && k.id !== kunde.id && !unsicher) {
              setzeFenster({ art: 'umordnen', kunde: k });
              return;
            }
            schliessen();
            void tue(() => kundeSetzen(vorgang, k.id, kunde !== null && k.id !== kunde.id ? BERICHTIGT : null));
          }}
        />
      )}

      {(fenster?.art === 'umordnen' || fenster?.art === 'loesen') && (
        <Dialog
          titel={fenster.art === 'umordnen' ? 'Anderem Kunden zuordnen' : 'Zuordnung lösen'}
          unterzeile={fenster.art === 'umordnen'
            ? `Von ${kunde?.kundennummer ?? ''} ${kunde?.name ?? ''} zu ${fenster.kunde.kundennummer} ${fenster.kunde.name}`
            : 'Der Vorgang geht in die Klärungsliste.'}
          breite="schmal"
          beiSchliessen={schliessen}
        >
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (grund.trim() === '') return;
              const f = fenster;
              void tue(() => (f.art === 'umordnen'
                ? kundeSetzen(vorgang, f.kunde.id, grund.trim())
                : kundeLoesen(vorgang, grund.trim()))).then((ok) => {
                if (ok) schliessen();
              });
            }}
          >
            <div className="feld">
              <label htmlFor="zuordnung-grund">Grund</label>
              <input id="zuordnung-grund" value={grund} maxLength={1000} onChange={(e) => setzeGrund(e.target.value)} />
              <p className="feld__hilfe">Steht im Verlauf der Zuordnung.</p>
            </div>
            <div className="knopfreihe">
              <button type="button" className="knopf" onClick={schliessen}>Abbrechen</button>
              <button type="submit" className="knopf knopf--haupt" disabled={laeuft || grund.trim() === ''}>
                {fenster.art === 'umordnen' ? 'Umordnen' : 'Lösen'}
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {fenster?.art === 'anlegen' && (
        <Dialog
          titel={vertrieb ? 'Als Interessent anlegen' : 'Kunde anlegen'}
          unterzeile="Vorbelegt aus Absender und Signatur der Nachricht"
          breite="breit"
          beiSchliessen={schliessen}
        >
          {fenster.vorbelegung.dubletten.length > 0 && (
            <div className="warnung" role="status">
              <strong>Diese Kunden könnten dieselben sein:</strong>
              <ul>
                {fenster.vorbelegung.dubletten.map((d) => (
                  <li key={d.kunde}>
                    {d.kundennummer} {d.name}{d.ort === null ? '' : `, ${d.ort}`}{' '}
                    <span className="hilfe">({d.grund})</span>{' '}
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laeuft}
                      onClick={() => {
                        void tue(() => kundeSetzen(vorgang, d.kunde, null)).then((ok) => {
                          if (ok) schliessen();
                        });
                      }}
                    >
                      Diesem zuordnen
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <KundeAnlegen
            vorgabe={vorgabeAus(fenster.vorbelegung)}
            einzeln
            beiAbbruch={schliessen}
            beiAngelegt={(id) => {
              void tue(() => kundeSetzen(vorgang, id, null, true)).then(() => schliessen());
            }}
          />
        </Dialog>
      )}
    </section>
  );
}
